import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { customersApi } from '../../api';
import { Customer } from '../../types';
import { formatDate } from '../../utils';

interface QBCustomersTabProps {
  clientId: string;
}

export const QBCustomersTab: React.FC<QBCustomersTabProps> = ({ clientId }) => {
  const toast = useRef<Toast>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCustomers();
  }, [clientId]);

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      const data = await customersApi.listCustomers(clientId);
      // Only show customers with QB IDs
      setCustomers(data.filter(c => c.qbo_customer_id));
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load customers',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const qbIdBodyTemplate = (rowData: Customer) => {
    if (rowData.qbo_customer_id) {
      return rowData.qbo_customer_id;
    }
    return <Tag value="Local only" severity="secondary" />;
  };

  const activeBodyTemplate = (rowData: Customer) => {
    if (rowData.is_active) {
      return <Tag value="Active" severity="success" />;
    }
    return <Tag value="Inactive" severity="warning" />;
  };

  return (
    <>
      <Toast ref={toast} />

      <DataTable
        value={customers}
        dataKey="id"
        loading={isLoading}
        paginator
        rows={50}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> customers
            </span>
          ),
        }}
        size="small"
        stripedRows
        scrollable
        scrollHeight="flex"
        emptyMessage="No customers found"
        sortField="name"
        sortOrder={1}
      >
        <Column
          field="name"
          header="Name"
          sortable
          style={{ width: '30%' }}
        />
        <Column
          field="qbo_customer_id"
          header="QB ID"
          body={qbIdBodyTemplate}
          sortable
          style={{ width: '20%' }}
        />
        <Column
          field="is_active"
          header="Status"
          body={activeBodyTemplate}
          sortable
          style={{ width: '15%' }}
        />
        <Column
          field="created_at"
          header="Created"
          body={(rowData) => formatDate(rowData.created_at)}
          sortable
          style={{ width: '17.5%' }}
        />
        <Column
          field="updated_at"
          header="Updated"
          body={(rowData) => formatDate(rowData.updated_at)}
          sortable
        />
      </DataTable>
    </>
  );
};
